import * as React from 'react';
import * as styles from '../segments.module.scss';
import { CircularProgress } from '@material-ui/core';
import { CollapsibleSidebar } from 'kagr-ui';
import FilterMenu from './filter-menu';
import OptionsFilterGroup from './options-filter-group';
import DateFilterGroup from './date-filter-group';
import MinMaxFilterGroup from './minmax-filter-group';
import { SegmentFilters, SegmentFiltersAdmin } from '../../../models/segments';
import { useSegmentFilters } from './useSegmentFilters';

interface Props {
  setActiveFilters: (filters: SegmentFilters | SegmentFiltersAdmin) => void;
  defaultActiveFilters: SegmentFilters | SegmentFiltersAdmin;
  isLoading: boolean;
  loadedFilters?: {
    clients?: {
      name: string;
      id: string;
    }[];
    users: {
      name: string;
      id: string;
    }[];
  };
}

const SegmentsSidebar = ({ setActiveFilters, defaultActiveFilters, isLoading, loadedFilters }: Props) => {
  const { state, dispatch, handleApply, handleClearAll } = useSegmentFilters(setActiveFilters, defaultActiveFilters);
  const [filtersCollapsed, setFiltersCollapsed] = React.useState<boolean>(true);
  const [isMinmaxValid, setIsMinmaxValid] = React.useState<boolean>(true);
  const formIsValid =
    (state.filters.startDate == null || state.filters.startDate.isValid()) &&
    (state.filters.endDate == null || state.filters.endDate.isValid()) &&
    isMinmaxValid;

  return (
    <CollapsibleSidebar
      collapsed={filtersCollapsed}
      setCollapsed={setFiltersCollapsed}
      initialCollapse={true}
      width="260px"
      collapsedWidth="70px"
      className={styles.sidebar}
    >
      <FilterMenu
        isLoading={isLoading}
        isCollapsed={filtersCollapsed}
        isInvalid={!formIsValid}
        activeFilterCount={state.filterCount}
        onApply={handleApply}
        onCancel={() => dispatch({ type: 'cancel' })}
        onClearAll={handleClearAll}
      >
        {isLoading && (
          <div className={styles.circularProgressContainer}>
            <CircularProgress size="20px" />
          </div>
        )}
        {loadedFilters && (
          <React.Fragment>
            {loadedFilters.clients && (
              <OptionsFilterGroup
                title="Teams"
                options={loadedFilters.clients}
                selected={'teams' in state.filters && state.filters.teams}
                updateSelectedFilters={selected => dispatch({ type: 'setFilters', filters: { teams: selected } })}
              />
            )}
            <DateFilterGroup
              title="Modified Date"
              startDate={state.filters.startDate}
              endDate={state.filters.endDate}
              onStartDateChange={date => dispatch({ type: 'setFilters', filters: { startDate: date } })}
              onEndDateChange={date => dispatch({ type: 'setFilters', filters: { endDate: date } })}
            />
            <MinMaxFilterGroup
              title="# of Customers"
              min={state.filters.min}
              max={state.filters.max}
              setMin={min => dispatch({ type: 'setFilters', filters: { min } })}
              setMax={max => dispatch({ type: 'setFilters', filters: { max } })}
              isValid={isMinmaxValid}
              setIsValid={setIsMinmaxValid}
            />
            <OptionsFilterGroup
              title="Created By"
              options={loadedFilters.users}
              selected={state.filters.users}
              updateSelectedFilters={selected => dispatch({ type: 'setFilters', filters: { users: selected } })}
            />
            <OptionsFilterGroup
              options={[{ name: 'Shared', id: 'true' }, { name: 'Not shared', id: 'false' }]}
              title="Sharing"
              selected={state.filters.isShared}
              updateSelectedFilters={selected =>
                dispatch({ type: 'setFilters', filters: { isShared: selected as ('true' | 'false')[] } })
              }
            />
          </React.Fragment>
        )}
      </FilterMenu>
    </CollapsibleSidebar>
  );
};

export default SegmentsSidebar;
